/**
 * PlayerRankingsPage.jsx
 * Leaderboard ranked by wins — Overall + per-sport tabs.
 * Clicking a player opens a drilldown showing all their individual matches across sports.
 */
import { useEffect, useState, useCallback, useRef } from 'react';
import { Trophy, RefreshCw, Star, ChevronLeft, ChevronDown, Clock, ArrowLeft, Search, X, Share2 } from 'lucide-react';
import { downloadCSV, printPage } from '@/lib/exportUtils';
import ExportBar from '@/components/ExportBar';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import useAuthStore from '@/stores/authStore';
import { toast } from 'sonner';
import SportIcon, { SPORT_LABELS } from '@/components/SportIcon';

const SPORT_ICONS = {
  table_tennis:'🏓', badminton:'🏸', chess:'♟️',
  carrom:'🎯', pickleball:'🥒', squash:'🎾',
  cricket:'🏏', football:'⚽', default:'🏅',
};

const MEDALS = ['🥇', '🥈', '🥉'];
const CAT_COLORS = {
  champion: '#f5c518', advanced: '#3b82f6', non_playing: '#8a93a3',
};

const RESULT_CONFIG = {
  win:  { label:'W', color:'#22c55e', bg:'rgba(34,197,94,0.1)'  },
  loss: { label:'L', color:'#ef4444', bg:'rgba(239,68,68,0.1)'  },
  draw: { label:'D', color:'#f5c518', bg:'rgba(245,197,24,0.1)' },
  bye:  { label:'BYE', color:'#8a93a3', bg:'rgba(138,147,163,0.1)' },
  pending: { label:'?', color:'#8a93a3', bg:'rgba(138,147,163,0.08)' },
};

const fmtDate = (iso) => iso
  ? new Date(iso).toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric', timeZone:'Asia/Kolkata' })
  : '—';

// ── Player Match Drilldown ────────────────────────────────────────────────────
function PlayerDrilldown({ player, orgSlug, token, onBack, onShowCard }) {
  const [matchData, setMatchData] = useState(null);
  const [loading, setLoading]     = useState(true);
  const [sportFilter, setSportFilter] = useState('all');
  const [expandedMatches, setExpandedMatches] = useState(new Set());
  const toggleMatch = (idx) => setExpandedMatches(prev => {
    const next = new Set(prev); next.has(idx) ? next.delete(idx) : next.add(idx); return next;
  });

  useEffect(() => {
    fetch(`/api/orgs/${orgSlug}/players/${player.player_id}/matches`,
      { headers: { Authorization: `Bearer ${token()}` } })
      .then(r => r.ok ? r.json() : Promise.reject(r))
      .then(d => setMatchData(d))
      .catch(() => toast.error('Failed to load matches'))
      .finally(() => setLoading(false));
  }, [player.player_id, orgSlug]); // eslint-disable-line react-hooks/exhaustive-deps

  const allMatches = matchData?.matches || [];
  const filtered   = sportFilter === 'all'
    ? allMatches
    : allMatches.filter(m => m.sport_code === sportFilter);
  const sports     = matchData?.sports || [];
  const summary    = matchData?.summary || {};

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <div className="flex items-center gap-3 mb-5">
        <button onClick={onBack}
          className="flex items-center gap-1 text-[#8a93a3] hover:text-[#f0f2f7] transition-colors">
          <ArrowLeft className="h-4 w-4" />
          <span className="text-sm">Rankings</span>
        </button>
        <span className="text-[#252a37]">/</span>
        <span className="text-sm font-bold text-[#f0f2f7]">{player.player_name}</span>
      </div>

      {/* Player card */}
      <div className="flex items-center gap-4 mb-6 p-4 bg-[#161a26] border border-[#252a37] rounded-2xl">
        <div className="h-14 w-14 rounded-2xl flex items-center justify-center font-black text-white text-xl overflow-hidden border-2"
          style={{ borderColor: (CAT_COLORS[player.category]||'#3b82f6')+'66',
                   backgroundColor: (CAT_COLORS[player.category]||'#3b82f6')+'33' }}>
          {player.photo_url?.startsWith('data:')
            ? <img src={player.photo_url} className="h-full w-full object-cover rounded-2xl" alt=""/>
            : <span style={{ color: CAT_COLORS[player.category]||'#3b82f6' }}>{player.player_name?.[0]?.toUpperCase()}</span>}
        </div>
        <div className="flex-1">
          <h2 className="text-xl font-black text-[#f0f2f7]">{player.player_name}</h2>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            {player.is_platinum && <span className="text-[#f5c518] text-xs flex items-center gap-0.5"><Star className="h-3 w-3"/>Platinum</span>}
            <span className="text-xs text-[#8a93a3]">{player.played}P · {player.wins}W · {player.win_pct}% win rate</span>
          </div>
        </div>
        {/* Overall stats pills */}
        <div className="flex flex-col gap-1.5 items-end">
          <div className="flex gap-2">
            {[
              { label:'W', value: player.wins,   color:'#22c55e' },
              { label:'L', value: player.played - player.wins, color:'#ef4444' },
            ].map(({ label, value, color }) => (
              <div key={label} className="text-center px-3 py-2 bg-[#1d2230] rounded-xl">
                <p className="text-lg font-black tabular-nums" style={{ color }}>{value}</p>
                <p className="text-[10px] text-[#8a93a3]">{label}</p>
              </div>
            ))}
          </div>
          {/* Player Card button */}
          <button
            onClick={() => onShowCard && onShowCard(player)}
            className="text-[10px] text-[#f5c518] hover:text-[#f7d24a] flex items-center gap-1 px-2 py-1 rounded-lg bg-[rgba(245,197,24,0.08)] border border-[rgba(245,197,24,0.2)]"
          >
            <Share2 className="h-3 w-3"/>
            Player Card
          </button>
        </div>
      </div>

      {/* Sport summary pills */}
      {Object.keys(summary).length > 0 && (
        <div className="flex gap-2 overflow-x-auto pb-2 mb-5">
          <button onClick={() => setSportFilter('all')}
            className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${
              sportFilter === 'all' ? 'bg-[#f5c518] text-[#0b0f18] border-[#f5c518]'
                                    : 'bg-[#161a26] text-[#8a93a3] border-[#252a37] hover:text-[#f0f2f7]'
            }`}>
            All Sports ({allMatches.length})
          </button>
          {sports.map(sc => {
            const s = summary[sc] || {};
            return (
              <button key={sc} onClick={() => setSportFilter(sc)}
                className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${
                  sportFilter === sc ? 'bg-[#f5c518] text-[#0b0f18] border-[#f5c518]'
                                     : 'bg-[#161a26] text-[#8a93a3] border-[#252a37] hover:text-[#f0f2f7]'
                }`}>
                <SportIcon sport={sc} size={21}/>
                {SPORT_LABELS[sc]||sc}
                <span className="bg-[rgba(34,197,94,0.15)] text-[#22c55e] px-1 rounded text-[10px]">
                  {s.wins||0}W/{s.played||0}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {/* Match list */}
      {loading ? (
        <div className="space-y-2">{[1,2,3,4].map(i=><Skeleton key={i} className="h-16 bg-[#1d2230] rounded-xl"/>)}</div>
      ) : filtered.length === 0 ? (
        <Card className="bg-[#161a26] border-[#252a37]">
          <CardContent className="p-10 text-center">
            <Trophy className="h-8 w-8 text-[#8a93a3] mx-auto mb-2"/>
            <p className="text-[#f0f2f7] font-semibold text-sm mb-1">No individual match records</p>
            <p className="text-[#8a93a3] text-xs max-w-xs mx-auto">
              Individual match history is recorded when matches are scored using the
              Scorer Portal with player names selected for each match.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {filtered.map((m, i) => {
            const rc = RESULT_CONFIG[m.result] || RESULT_CONFIG.pending;
            const isPlat = m.tier === 'platinum';
            const isSportSummary = m.match_type === 'sport_summary';
            const isExpanded = expandedMatches.has(i);
            const isResultSport = ['chess','carrom'].includes(m.sport_code);
            const games = (m.games||[]).filter(g => g.home || g.away);

            const scoreDisplay = (() => {
              if (isResultSport) return m.result === 'win' ? 'Win' : m.result === 'loss' ? 'Loss' : m.result === 'draw' ? 'Draw' : m.result === 'bye' ? 'BYE' : null;
              if (m.my_score !== null && m.my_score !== undefined) return `${m.my_score}–${m.opp_score}`;
              return null;
            })();

            return (
              <div key={i} className={`rounded-xl border overflow-hidden ${
                isSportSummary ? 'bg-[#10131c] border-[#252a37] opacity-90'
                : isPlat ? 'bg-[#161a26] border-[rgba(245,197,24,0.25)]'
                         : 'bg-[#10131c] border-[#252a37]'
              }`}>
                {/* Clickable row */}
                <button className="w-full text-left flex items-center gap-3 px-4 py-3 hover:bg-[#161a26] transition-colors"
                  onClick={() => toggleMatch(i)}>
                  {/* Result badge */}
                  <div className="h-9 w-9 rounded-xl flex-shrink-0 flex items-center justify-center font-black text-xs border-2"
                    style={{ color: rc.color, borderColor: `${rc.color}44`, backgroundColor: rc.bg }}>
                    {rc.label}
                  </div>

                  {/* Sport + slot */}
                  <div className="flex-shrink-0 text-center w-10">
                    <SportIcon sport={m.sport_code} size={30}/>
                    {!isSportSummary && <p className="text-[9px] text-[#8a93a3] mt-0.5 font-mono">{m.slot}</p>}
                    {isPlat && <span className="text-[8px] text-[#f5c518] font-bold">★PLAT</span>}
                  </div>

                  {/* Opponent */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-sm font-semibold text-[#f0f2f7] truncate">
                        vs {(() => {
                          const DOUBLES_SPORTS = new Set(['badminton', 'carrom', 'pickleball']);
                          const isDoubles = m.match_type?.toLowerCase() === 'doubles' ||
                                            DOUBLES_SPORTS.has(m.sport_code) ||
                                            (m.opp_players?.length > 1);
                          if (isDoubles && m.opp_players?.length > 1)
                            return m.opp_players.join(' / ');
                          return m.opp_player || m.opp_team || 'Opponent';
                        })()}
                      </span>
                      {isSportSummary && (
                        <span className="text-[9px] text-[#8a93a3] bg-[#1d2230] px-1.5 py-0.5 rounded">sport result</span>
                      )}
                      {!isSportSummary && m.opp_team && m.opp_player !== m.opp_team && (
                        <span className="text-[10px] text-[#8a93a3] bg-[#1d2230] px-1.5 py-0.5 rounded truncate">{m.opp_team}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      <span className="text-[10px] text-[#8a93a3]">{m.tournament_name}</span>
                      {m.match_label && (
                        <span className="text-[10px] font-bold text-[#f5c518] bg-[rgba(245,197,24,0.1)] px-1.5 rounded">{m.match_label}</span>
                      )}
                      {!m.match_label && m.round_type && m.round_type !== 'group' && (
                        <span className="text-[10px] font-bold text-[#f5c518] bg-[rgba(245,197,24,0.1)] px-1.5 rounded">{m.round_type?.toUpperCase()}</span>
                      )}
                      {m.scheduled_at && (
                        <span className="text-[10px] text-[#8a93a3] flex items-center gap-0.5">
                          <Clock className="h-2.5 w-2.5"/>{fmtDate(m.scheduled_at)}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Score */}
                  {scoreDisplay && (
                    <div className="text-right flex-shrink-0">
                      <p className="text-sm font-black tabular-nums" style={{ color: rc.color }}>{scoreDisplay}</p>
                      {!isSportSummary && games.length > 0 && (
                        <p className="text-[9px] text-[#8a93a3] font-mono">
                          {games.map(g => `${g.home}-${g.away}`).join(' ')}
                        </p>
                      )}
                    </div>
                  )}

                  <ChevronDown className={`h-3.5 w-3.5 text-[#8a93a3] flex-shrink-0 ml-1 transition-transform ${isExpanded ? 'rotate-180' : ''}`}/>
                </button>

                {/* Expanded detail */}
                {isExpanded && (
                  <div className="px-4 pb-3 border-t border-[#252a37] pt-3 space-y-2 bg-[#0a0c13]">
                    {/* Player vs Player headline — show both partners for doubles */}
                    <div className="flex items-center justify-between gap-2 py-2 px-3 bg-[#161a26] rounded-xl">
                      <div className="flex-1 text-center min-w-0">
                        <p className={`text-sm font-black truncate ${m.result==='win'?'text-[#22c55e]':'text-[#f0f2f7]'}`}>
                          {m.my_player || player.player_name}
                        </p>
                        {/* Show doubles partner */}
                        {(() => {
                          const DS = new Set(['badminton','carrom','pickleball']);
                          const isDbl = m.match_type?.toLowerCase()==='doubles' || DS.has(m.sport_code) || (m.my_players?.length>1);
                          if (!isDbl) return null;
                          const partner = (m.my_players||[]).filter(p => p && p !== m.my_player)[0];
                          return <p className="text-[10px] text-[#8a93a3] mt-0.5">{partner ? `& ${partner}` : '& (partner not recorded)'}</p>;
                        })()}
                        {m.result==='win' && <p className="text-[9px] text-[#22c55e] font-bold">🏆 Winner</p>}
                      </div>
                      <div className="flex-shrink-0 px-3 text-center">
                        {isResultSport ? (
                          <p className="text-base font-black" style={{ color: rc.color }}>{scoreDisplay}</p>
                        ) : scoreDisplay ? (
                          <p className="text-xl font-black text-[#f5c518] tabular-nums">{scoreDisplay}</p>
                        ) : <p className="text-[#8a93a3] text-sm">–</p>}
                        <p className="text-[9px] text-[#8a93a3]">{SPORT_LABELS[m.sport_code]||m.sport_code}</p>
                      </div>
                      <div className="flex-1 text-center min-w-0">
                        <p className={`text-sm font-black truncate ${m.result==='loss'?'text-[#22c55e]':'text-[#f0f2f7]'}`}>
                          {(m.opp_players||[]).length > 1 ? m.opp_players[0] : (m.opp_player || m.opp_team || 'Opponent')}
                        </p>
                        {(() => {
                          const DS = new Set(['badminton','carrom','pickleball']);
                          const isDbl = m.match_type?.toLowerCase()==='doubles' || DS.has(m.sport_code) || (m.opp_players?.length>1);
                          if (!isDbl) return null;
                          const oppPartner = (m.opp_players||[]).length > 1 ? m.opp_players[1] : null;
                          return <p className="text-[10px] text-[#8a93a3] mt-0.5">{oppPartner ? `& ${oppPartner}` : '& (partner not recorded)'}</p>;
                        })()}
                        {m.result==='loss' && <p className="text-[9px] text-[#22c55e] font-bold">🏆 Winner</p>}
                      </div>
                    </div>
                    {/* Game-by-game (TT) */}
                    {games.length > 0 && (
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-[9px] text-[#8a93a3] uppercase tracking-wide">Games:</span>
                        {games.map((g, gi) => (
                          <span key={gi} className="text-[10px] font-mono bg-[#1d2230] border border-[#252a37] px-1.5 py-0.5 rounded text-[#f0f2f7]">
                            {g.home}–{g.away}
                          </span>
                        ))}
                      </div>
                    )}
                    {/* Meta */}
                    <div className="flex items-center justify-between text-[9px] text-[#8a93a3] pt-0.5">
                      <span className="truncate">{m.tournament_name}</span>
                      <span className="flex items-center gap-1 flex-shrink-0 ml-2">
                        {m.match_label && <span className="font-mono text-[#f5c518]">{m.match_label}</span>}
                        {m.scheduled_at && <span className="flex items-center gap-0.5"><Clock className="h-2 w-2"/>{fmtDate(m.scheduled_at)}</span>}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function RankBadge({ rank }) {
  if (rank <= 3) return (
    <span className="text-2xl leading-none">{MEDALS[rank - 1]}</span>
  );
  return (
    <span className="text-sm font-black text-[#8a93a3] tabular-nums w-8 text-center">{rank}</span>
  );
}

function PlayerRow({ player, rank, activeSport, onClick }) {
  const pts = activeSport === 'overall'
    ? { wins: player.wins, played: player.played, win_pct: player.win_pct }
    : (player.per_sport?.[activeSport] || { wins: 0, played: 0, win_pct: 0 });

  const catColor = CAT_COLORS[player.category] || '#8a93a3';

  return (
    <div
      onClick={onClick}
      className={`flex items-center gap-3 px-4 py-3 border-b border-[#252a37]/60 last:border-0 transition-colors cursor-pointer
        ${rank === 1 ? 'bg-[rgba(245,197,24,0.04)] hover:bg-[rgba(245,197,24,0.07)]' : 'hover:bg-[#161a26]'}`}>

      {/* Rank */}
      <div className="flex-shrink-0 w-8 flex items-center justify-center">
        <RankBadge rank={rank} />
      </div>

      {/* Avatar */}
      <div className="h-10 w-10 rounded-xl flex-shrink-0 flex items-center justify-center font-black text-white text-sm overflow-hidden border-2"
        style={{ backgroundColor: catColor + '33', borderColor: catColor + '66' }}>
        {player.photo_url?.startsWith('data:')
          ? <img src={player.photo_url} className="h-full w-full object-cover rounded-xl" alt="" />
          : <span style={{ color: catColor }}>{player.player_name?.[0]?.toUpperCase()}</span>}
      </div>

      {/* Name + badges */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className={`font-bold text-sm truncate ${rank === 1 ? 'text-[#f5c518]' : 'text-[#f0f2f7]'}`}>
            {player.player_name}
          </span>
          {player.is_platinum && (
            <Star className="h-3 w-3 text-[#f5c518] flex-shrink-0" />
          )}
        </div>
        <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
          {player.sports?.slice(0, 4).map(s => (
            <span key={s} className="text-xs" title={SPORT_LABELS[s] || s}>
              <SportIcon sport={s} size={16}/>
            </span>
          ))}
        </div>
      </div>

      {/* Stats */}
      <div className="flex items-center gap-4 flex-shrink-0 text-right">
        <div className="text-center hidden sm:block">
          <p className="text-[10px] text-[#8a93a3] uppercase tracking-wide">Played</p>
          <p className="text-sm font-bold text-[#f0f2f7]">{pts.played}</p>
        </div>
        <div className="text-center">
          <p className="text-[10px] text-[#8a93a3] uppercase tracking-wide">Wins</p>
          <p className={`text-lg font-black tabular-nums ${rank === 1 ? 'text-[#f5c518]' : 'text-[#22c55e]'}`}>
            {pts.wins}
          </p>
        </div>
        <div className="text-center w-14">
          <p className="text-[10px] text-[#8a93a3] uppercase tracking-wide">Win%</p>
          <div className="relative h-1.5 bg-[#1d2230] rounded-full mt-1 overflow-hidden">
            <div className="absolute inset-y-0 left-0 rounded-full transition-all"
              style={{
                width: `${pts.win_pct}%`,
                backgroundColor: pts.win_pct >= 60 ? '#22c55e' : pts.win_pct >= 40 ? '#f5c518' : '#ef4444',
              }} />
          </div>
          <p className="text-[10px] font-bold mt-0.5"
            style={{ color: pts.win_pct >= 60 ? '#22c55e' : pts.win_pct >= 40 ? '#f5c518' : '#8a93a3' }}>
            {pts.win_pct}%
          </p>
        </div>
      </div>
    </div>
  );
}

export default function PlayerRankingsPage() {
  const { activeOrgSlug, user } = useAuthStore();
  const orgSlug = user?.memberships?.[0]?.slug || activeOrgSlug || 'btcc';
  const token = () => localStorage.getItem('btccp_access_token');

  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [activeSport, setActiveSport] = useState('overall');
  const [drillPlayer, setDrillPlayer] = useState(null); // player to drill into
  const [playerSearch, setPlayerSearch] = useState(''); // search filter
  const [cardPlayer, setCardPlayer] = useState(null);   // player card modal

  const fetchRankings = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`/api/orgs/${orgSlug}/rankings`,
        { headers: { Authorization: `Bearer ${token()}` } });
      if (res.ok) setData(await res.json());
      else toast.error('Failed to load rankings');
    } catch (_) { toast.error('Failed to load rankings'); }
    finally { setLoading(false); }
  }, [orgSlug]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchRankings(); }, [fetchRankings]);

  const exportCSV = () => {
    const headers = ['Rank','Player','Sports','Played','Wins','Losses','Win%',
                     ...sports.map(s => (SPORT_LABELS[s]||s).toUpperCase() + ' W'),
                     ...sports.map(s => (SPORT_LABELS[s]||s).toUpperCase() + ' P')];
    const rows = rankings.map((p, i) => [
      i+1, p.player_name,
      Object.keys(p.per_sport||{}).map(s => SPORT_LABELS[s]||s).join('; '),
      p.played, p.wins, p.losses, `${p.win_pct}%`,
      ...sports.map(s => p.per_sport?.[s]?.wins ?? 0),
      ...sports.map(s => p.per_sport?.[s]?.played ?? 0),
    ]);
    downloadCSV('player_rankings', headers, rows);
    toast.success('Rankings exported!');
  };

  const rankings = data?.rankings || [];
  const sports   = data?.sports   || [];

  // For sport-specific tabs, only show players who have played that sport
  const filteredRankings = (activeSport === 'overall'
    ? rankings
    : rankings
        .filter(p => (p.per_sport?.[activeSport]?.wins ?? 0) > 0 ||
                     (p.per_sport?.[activeSport]?.played ?? 0) > 0)
        .sort((a, b) => {
          const wa = a.per_sport?.[activeSport]?.wins ?? 0;
          const wb = b.per_sport?.[activeSport]?.wins ?? 0;
          if (wb !== wa) return wb - wa;
          const pa = a.per_sport?.[activeSport]?.win_pct ?? 0;
          const pb = b.per_sport?.[activeSport]?.win_pct ?? 0;
          return pb - pa;
        })
  ).filter(p =>
    !playerSearch ||
    (p.player_name || '').toLowerCase().includes(playerSearch.toLowerCase())
  );

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="player-rankings-page">

      {/* Player drilldown — shown when player is tapped */}
      {drillPlayer && (
        <PlayerDrilldown
          player={drillPlayer}
          orgSlug={orgSlug}
          token={token}
          onBack={() => setDrillPlayer(null)}
          onShowCard={setCardPlayer}
        />
      )}

      {/* Rankings list */}
      {!drillPlayer && (
      <>{/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-[rgba(245,197,24,0.1)] flex items-center justify-center">
            <Trophy className="h-5 w-5 text-[#f5c518]" />
          </div>
          <div>
            <h1 className="text-2xl font-black text-[#f0f2f7]">Player Rankings</h1>
            <p className="text-sm text-[#8a93a3]">Ranked by wins across all tournaments</p>
          </div>
        </div>
        <button onClick={fetchRankings}
          className="p-2 rounded-lg text-[#8a93a3] hover:text-[#f0f2f7] hover:bg-[#161a26] transition-colors">
          <RefreshCw className="h-4 w-4" />
        </button>
        <ExportBar label="Rankings" onCSV={exportCSV} onPDF={printPage}/>
      </div>

      {/* Search + sport tabs */}
      {/* Search bar */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8a93a3]"/>
        <Input
          value={playerSearch}
          onChange={e => setPlayerSearch(e.target.value)}
          placeholder="Search player name…"
          className="pl-9 pr-8 bg-[#161a26] border-[#252a37] text-[#f0f2f7] placeholder:text-[#4a5568] focus:border-[#f5c518] focus-visible:ring-0"
        />
        {playerSearch && (
          <button onClick={() => setPlayerSearch('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8a93a3] hover:text-[#f0f2f7] text-xs">
            <X className="h-3.5 w-3.5"/>
          </button>
        )}
      </div>

      {/* Sport tabs */}
      <div className="flex gap-2 overflow-x-auto pb-3 -mx-1 px-1 mb-5">
        <button onClick={() => setActiveSport('overall')}
          className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${
            activeSport === 'overall'
              ? 'bg-[#f5c518] text-[#0b0f18] border-[#f5c518]'
              : 'bg-[#161a26] text-[#8a93a3] border-[#252a37] hover:text-[#f0f2f7]'
          }`}>
          🏆 Overall
        </button>
        {sports.map(sc => (
          <button key={sc} onClick={() => setActiveSport(sc)}
            className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${
              activeSport === sc
                ? 'bg-[#f5c518] text-[#0b0f18] border-[#f5c518]'
                : 'bg-[#161a26] text-[#8a93a3] border-[#252a37] hover:text-[#f0f2f7]'
            }`}>
            <SportIcon sport={sc} size={54} /> {SPORT_LABELS[sc] || sc}
          </button>
        ))}
      </div>

      {/* Rankings table */}
      {loading ? (
        <div className="space-y-3">
          {[1,2,3,4,5,6].map(i => <Skeleton key={i} className="h-16 bg-[#1d2230] rounded-xl" />)}
        </div>
      ) : filteredRankings.length === 0 ? (
        <Card className="bg-[#161a26] border-[#252a37]">
          <CardContent className="p-12 text-center">
            <Trophy className="h-10 w-10 text-[#8a93a3] mx-auto mb-3" />
            <p className="text-[#f0f2f7] font-semibold">No rankings yet</p>
            <p className="text-sm text-[#8a93a3] mt-1">
              {activeSport === 'overall'
                ? 'Complete some matches to see player rankings'
                : `No ${SPORT_LABELS[activeSport] || activeSport} results yet`}
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Top 3 podium */}
          {filteredRankings.length >= 3 && (
            <div className="grid grid-cols-3 gap-3 mb-5">
              {[1, 0, 2].map(idx => {
                const p = filteredRankings[idx];
                if (!p) return <div key={idx} />;
                const pts = activeSport === 'overall'
                  ? { wins: p.wins, played: p.played }
                  : (p.per_sport?.[activeSport] || { wins: 0, played: 0 });
                const isFirst = idx === 0;
                return (
                  <Card key={p.player_id}
                    className={`border text-center transition-all ${
                      isFirst
                        ? 'bg-[rgba(245,197,24,0.06)] border-[rgba(245,197,24,0.35)] scale-105'
                        : 'bg-[#161a26] border-[#252a37]'
                    }`}>
                    <CardContent className="p-4">
                      <div className="text-3xl mb-2">{MEDALS[idx]}</div>
                      <div className="h-12 w-12 rounded-xl mx-auto mb-2 flex items-center justify-center font-black text-white text-lg overflow-hidden border-2"
                        style={{ borderColor: isFirst ? '#f5c518' : '#252a37',
                                 backgroundColor: (CAT_COLORS[p.category] || '#3b82f6') + '33' }}>
                        {p.photo_url?.startsWith('data:')
                          ? <img src={p.photo_url} className="h-full w-full object-cover rounded-xl" alt="" />
                          : <span style={{ color: CAT_COLORS[p.category] || '#3b82f6' }}>{p.player_name?.[0]?.toUpperCase()}</span>}
                      </div>
                      <p className={`text-xs font-bold truncate ${isFirst ? 'text-[#f5c518]' : 'text-[#f0f2f7]'}`}>
                        {p.player_name}
                      </p>
                      <p className={`text-2xl font-black mt-1 ${isFirst ? 'text-[#f5c518]' : 'text-[#22c55e]'}`}>
                        {pts.wins}
                      </p>
                      <p className="text-[10px] text-[#8a93a3]">{pts.played} played</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          {/* Full list */}
          <Card className="bg-[#10131c] border-[#252a37] overflow-hidden">
            <div className="px-4 py-2.5 border-b border-[#252a37] flex items-center justify-between">
              <span className="text-xs font-bold text-[#8a93a3] uppercase tracking-wider">
                {activeSport === 'overall' ? 'All Sports Combined' : `${SPORT_ICONS[activeSport] || ''} ${SPORT_LABELS[activeSport] || activeSport}`}
              </span>
              <span className="text-xs text-[#8a93a3]">{filteredRankings.length} players</span>
            </div>
            {filteredRankings.map((player, i) => (
              <PlayerRow
                key={player.player_id}
                player={player}
                rank={i + 1}
                activeSport={activeSport}
                onClick={() => setDrillPlayer(player)}
              />
            ))}
          </Card>
        </>
      )}
      </>
      )}

      {/* ── Player Card Modal ── */}
      {cardPlayer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          onClick={() => setCardPlayer(null)}>
          <div onClick={e => e.stopPropagation()} className="w-full max-w-sm">
            {/* Card — designed for screenshot sharing */}
            <div id="player-card-screenshot" className="relative rounded-3xl overflow-hidden bg-[#0b0f18] border-2 border-[rgba(245,197,24,0.4)] shadow-[0_0_40px_rgba(245,197,24,0.15)]">
              {/* Gold top accent */}
              <div className="h-1.5 w-full bg-gradient-to-r from-[#f5c518] via-[#f7d24a] to-[#f5c518]"/>

              {/* Header */}
              <div className="px-5 pt-5 pb-4">
                <div className="flex items-center gap-4">
                  {/* Avatar */}
                  <div className="h-20 w-20 rounded-2xl flex-shrink-0 flex items-center justify-center font-black text-3xl text-white overflow-hidden border-2 border-[rgba(245,197,24,0.3)]"
                    style={{ backgroundColor: CAT_COLORS[cardPlayer.category] || '#3b82f6' }}>
                    {cardPlayer.photo_url?.startsWith('data:')
                      ? <img src={cardPlayer.photo_url} className="h-full w-full object-cover" alt=""/>
                      : cardPlayer.player_name?.[0]?.toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xl font-black text-[#f0f2f7] leading-tight">{cardPlayer.player_name}</p>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      {cardPlayer.is_platinum && (
                        <span className="text-[10px] font-black text-[#f5c518] bg-[rgba(245,197,24,0.15)] px-2 py-0.5 rounded-full border border-[rgba(245,197,24,0.3)]">★ Platinum</span>
                      )}
                      <span className="text-[10px] text-[#8a93a3] capitalize">{cardPlayer.category || 'Advanced'}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Stats row */}
              <div className="grid grid-cols-3 gap-2 px-5 pb-4">
                {[
                  { label:'Played', value: cardPlayer.played,   color:'#f0f2f7' },
                  { label:'Wins',   value: cardPlayer.wins,     color:'#22c55e' },
                  { label:'Win%',   value:`${cardPlayer.win_pct}%`, color:'#f5c518' },
                ].map(s => (
                  <div key={s.label} className="bg-[#161a26] rounded-2xl py-3 text-center border border-[#252a37]">
                    <p className="text-2xl font-black" style={{ color: s.color }}>{s.value}</p>
                    <p className="text-[10px] text-[#8a93a3] uppercase tracking-wider mt-0.5">{s.label}</p>
                  </div>
                ))}
              </div>

              {/* Per-sport */}
              {Object.keys(cardPlayer.per_sport || {}).length > 0 && (
                <div className="px-5 pb-4">
                  <p className="text-[10px] text-[#8a93a3] uppercase tracking-widest font-bold mb-2">Sport Stats</p>
                  <div className="space-y-1.5">
                    {Object.entries(cardPlayer.per_sport).sort((a,b)=>b[1].wins-a[1].wins).map(([sc, ss]) => {
                      const pct = ss.win_pct || (ss.played > 0 ? Math.round(ss.wins/ss.played*100) : 0);
                      return (
                        <div key={sc} className="flex items-center gap-2.5 bg-[#161a26] rounded-xl px-3 py-2 border border-[#252a37]">
                          <span className="text-lg"><SportIcon sport={sc} size={28}/></span>
                          <span className="text-sm font-semibold text-[#f0f2f7] flex-1">{SPORT_LABELS[sc]||sc}</span>
                          <span className="text-sm font-black text-[#22c55e]">{ss.wins}W</span>
                          <span className="text-xs text-[#8a93a3]">{ss.played}P</span>
                          <span className="text-xs font-bold w-10 text-right" style={{ color: pct>=60?'#22c55e':pct>=40?'#f5c518':'#ef4444' }}>{pct}%</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Footer branding */}
              <div className="px-5 pb-4 flex items-center justify-between">
                <p className="text-[10px] text-[#8a93a3]/50 font-mono">BTCCP · Circles Sports League</p>
                <Trophy className="h-4 w-4 text-[#f5c518]/40"/>
              </div>
            </div>

            {/* Actions below card */}
            <div className="flex gap-3 mt-3">
              <button
                onClick={() => {
                  const url = `${window.location.origin}/player/${cardPlayer.player_id}`;
                  navigator.clipboard.writeText(url)
                    .then(() => toast.success('Link copied!'))
                    .catch(() => {});
                }}
                className="flex-1 h-11 rounded-xl bg-[rgba(245,197,24,0.1)] border border-[rgba(245,197,24,0.3)] text-[#f5c518] text-sm font-bold flex items-center justify-center gap-2">
                <Share2 className="h-4 w-4"/>
                Copy Link
              </button>
              <button onClick={() => setCardPlayer(null)}
                className="h-11 w-11 rounded-xl bg-[#1d2230] border border-[#252a37] text-[#8a93a3] flex items-center justify-center">
                <X className="h-4 w-4"/>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
